import React from "react";
import Sparkline from "@/components/Sparkline";
import LaymenBadge from "@/components/LaymenBadge";
import RecommendationsPanel from "@/components/RecommendationsPanel";
import * as Phosphor from "@phosphor-icons/react";
import {
  ndviStatus,
  ndviAnomaly,
  soilDeficitStatus,
  yieldStatus,
  priceStatus,
  forestStatus,
  regionHealth,
  healthBand,
  toneColor,
} from "@/lib/laymen";

/** One row per region with a plain-language rollup and health ring. */
export default function RegionPlainCard({ region, snap, testid }) {
  const m = snap?.metrics || {};
  const sat = snap?.satellite || {};
  const score = regionHealth(m);
  const band = healthBand(score);
  const bandColors = toneColor(band.level);

  const crop = region.crop;
  const cropIcon =
    crop === "wheat" ? Phosphor.Grains :
    crop === "corn" ? Phosphor.Plant :
    Phosphor.Tree;

  const items = [
    { key: "ndvi", title: "Vegetation", status: ndviStatus(m.ndvi_current), value: m.ndvi_current, unit: "NDVI" },
    { key: "anomaly", title: "Abweichung", status: ndviAnomaly(m.ndvi_anomaly), value: m.ndvi_anomaly?.toFixed?.(3), unit: "Δ NDVI" },
    { key: "soil", title: "Bodenwasser", status: soilDeficitStatus(m.soil_moisture_deficit), value: m.soil_moisture_current, unit: "m³/m³" },
    { key: "yield", title: "Ernte-Aussicht", status: yieldStatus(m.yield_forecast_index), value: m.yield_forecast_index?.toFixed?.(0), unit: "/100" },
    { key: "price", title: "Futures-Preis", status: priceStatus(m.price_change_7d_pct), value: `${m.price_change_7d_pct >= 0 ? "+" : ""}${m.price_change_7d_pct ?? "–"}%`, unit: "7 Tage" },
    crop === "forest_lake"
      ? { key: "forest", title: "Wald & See", status: forestStatus(m.forest_vitality_index), value: m.forest_vitality_index?.toFixed?.(0), unit: "/100" }
      : null,
  ].filter(Boolean);

  return (
    <div className="eid-card p-4 md:p-5 space-y-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span
            className="p-2 rounded-sm"
            style={{ background: bandColors.bg, color: bandColors.fg }}
          >
            {React.createElement(cropIcon, { size: 22, weight: "regular" })}
          </span>
          <div>
            <div className="eid-label">{region.public_label}</div>
            <h3 className="text-xl font-medium tracking-tight">{region.label}</h3>
            <p className="text-sm text-zinc-400">{region.description}</p>
          </div>
        </div>
        <HealthRing score={score} label={band.label} level={band.level} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map((it) => (
          <div
            key={it.key}
            className="border border-white/10 rounded-sm p-3 bg-[#121212]"
            data-testid={`plain-${region.id}-${it.key}`}
          >
            <div className="eid-label">{it.title}</div>
            <div className="flex items-baseline justify-between gap-2 mt-1">
              <span className="font-mono text-lg" style={{ color: toneColor(it.status.level).fg }}>
                {it.value ?? "–"}
              </span>
              <span className="text-[10px] font-mono text-zinc-500">{it.unit}</span>
            </div>
            <div className="mt-2">
              <LaymenBadge status={it.status} size="sm" testid={`badge-${region.id}-${it.key}`} />
            </div>
          </div>
        ))}
      </div>

      <div className="pt-2">
        <div className="eid-label mb-1">NDVI · 30 Tage</div>
        <Sparkline values={sat.ndvi} width={640} height={40} stroke={bandColors.dot} />
      </div>

      <RecommendationsPanel
        regionId={region.id}
        regionLabel={region.public_label}
        testid={`recs-${region.id}`}
      />
    </div>
  );
}

function HealthRing({ score, label, level }) {
  const c = toneColor(level);
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - score / 100);
  return (
    <div className="flex items-center gap-3" data-testid="health-ring">
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r={radius} fill="none" stroke="#141414" strokeWidth="6" />
        <circle
          cx="36" cy="36" r={radius}
          fill="none"
          stroke={c.dot}
          strokeWidth="6"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 36 36)"
          style={{ transition: "stroke-dashoffset 600ms ease-out" }}
        />
        <text x="36" y="41" textAnchor="middle" fontSize="17" fontFamily="JetBrains Mono" fill="#FFFFFF">
          {score}
        </text>
      </svg>
      <div className="text-right">
        <div className="eid-label">Gesundheit</div>
        <div className="text-sm font-medium" style={{ color: c.fg }}>
          {label}
        </div>
      </div>
    </div>
  );
}
